import React, { type ComponentProps, useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { TransparentModal, useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventOnFocus } from '@order/Telemetry';

import { LocationSearch, NotFoundView } from '../../components';
import { useSetProductDetailsLocation } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsLocationScreen = () => {
  const isFeatureEnabled = useFeatureFlag('CELS-4497-order-product-details-page-enabled'); // prettier-ignore
  const router = useRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    router.back();
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────

  if (isFeatureEnabled) {
    return <ProductDetailsLocationScreenEnabled dismissModal={dismissModal} />;
  }

  return <ProductDetailsLocationScreenDisabled dismissModal={dismissModal} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsLocationScreenEnabled = (props: Props) => {
  const { dismissModal } = props;

  const { setLastInteractedMenuStore } = useSetProductDetailsLocation();
  const noticeBanners = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  // ─── Route ───────────────────────────────────────────────────────────

  const { baseProductSlug } = useLocalSearchParams<{ baseProductSlug: string }>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const notifyAboutUnavailableLocation = useCallback(() => {
    noticeBanners.push(
      {
        id: UNAVAILABLE_LOCATION_ERROR_BANNER_ID,
        palette: 'caution',
        text: formatMessage(messages.unavailableLocationMessage),
      },
      true,
    );
  }, [formatMessage, noticeBanners]);

  const onLocationSelect = useCallback<NavigateToLocation>(
    (context) => {
      const { activeLocation } = context;

      noticeBanners.clear();

      if (!activeLocation) {
        notifyAboutUnavailableLocation();
        return;
      }

      setLastInteractedMenuStore({
        restaurantId: activeLocation.restaurantId,
        restaurantSlug: activeLocation.restaurantSlug,
        restaurantName: activeLocation.restaurantName,
      });

      dismissModal();
    },
    [noticeBanners, setLastInteractedMenuStore, dismissModal, notifyAboutUnavailableLocation],
  );

  const onDeliveryLocationSelect = useCallback<NavigateToDeliveryLocation>(
    (context) => {
      const { deliveryLocation } = context;

      noticeBanners.clear();

      if (!deliveryLocation) {
        notifyAboutUnavailableLocation();
        return;
      }

      setLastInteractedMenuStore({
        restaurantId: deliveryLocation.restaurantId,
        restaurantSlug: deliveryLocation.restaurantSlug,
        restaurantName: deliveryLocation.restaurantName,
        addressId: deliveryLocation.addressId,
        vendor: deliveryLocation.deliveryVendorName,
        vendorRestaurantId: deliveryLocation.deliveryVendorRestaurantId,
        deliveryFee: deliveryLocation.restaurantDeliveryFee,
      });

      dismissModal();
    },
    [dismissModal, noticeBanners, notifyAboutUnavailableLocation, setLastInteractedMenuStore],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({
    name: 'product-details.locations.viewed',
    payload: { baseProductSlug },
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size="large"
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      onClose={dismissModal}
    >
      <LocationSearch
        navigateToLocation={onLocationSelect}
        navigateToDeliveryLocation={onDeliveryLocationSelect}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal={true}
        onNavClose={dismissModal}
        withNavHeader
      />
    </TransparentModal>
  );
};

const ProductDetailsLocationScreenDisabled = (props: Props) => {
  return (
    <TransparentModal
      dialogMaxWidth={DIALOG_MAX_WIDTH}
      dialogMaxHeight={DIALOG_MAX_HEIGHT}
      onClose={props.dismissModal}
    >
      <NotFoundView withoutFooter />
    </TransparentModal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const UNAVAILABLE_LOCATION_ERROR_BANNER_ID = 'product-details.location-search.unavailable-location'; // prettier-ignore

const DIALOG_MAX_WIDTH = 1000;
const DIALOG_MAX_HEIGHT = 640;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  unavailableLocationMessage: {
    defaultMessage: 'Something went wrong. Please select a different location.',
    description: 'Product details | Location search | Unavailable location message', // prettier-ignore
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  dismissModal: () => void;
};

type NavigateToLocation = NonNullable<ComponentProps<typeof LocationSearch>['navigateToLocation']>;
type NavigateToDeliveryLocation = NonNullable<
  ComponentProps<typeof LocationSearch>['navigateToDeliveryLocation']
>;
